import { getStoredExpertAuthData } from 'utils/expertAuth';
import {
  getStoredWorkspaceId,
  setStoredWorkspaceId,
  clearStoredWorkspaceId,
} from 'utils/workspaceStorage';
import create from 'zustand';
import { getDefaultEmail } from 'utils/accountant';

export interface WorkspaceGuard {
  workspaceId: string | null;
  userEmail: string | null;
  hasWorkspaceContext: boolean;
}

/**
 * Workspace guard function
 * Provides workspace context synchronously for conditional query enabling.
 * Always returns current localStorage values without React rendering timing issues.
 *
 * @example
 * ```tsx
 * const { hasWorkspaceContext, workspaceId } = useWorkspaceDetails();
 *
 * const query = useQuery({
 *   queryKey: ['customers', workspaceId],
 *   queryFn: getClients,
 *   enabled: hasWorkspaceContext,
 * });
 * ```
 */

const useWorkspaceDetails = create<
  WorkspaceGuard & {
    setWorkspaceId: (id: string) => void;
    clearWorkspaceId: () => void;
    syncUserEmail: () => void;
  }
>((set, get) => {
  const authData = getStoredExpertAuthData();
  const workspaceId = getStoredWorkspaceId();
  const userEmail = getDefaultEmail(authData?.accountant?.emails) || null;
  const hasWorkspaceContext = Boolean(workspaceId && userEmail);

  return {
    workspaceId,
    userEmail,
    hasWorkspaceContext,
    setWorkspaceId: (id: string) => {
      setStoredWorkspaceId(id);
      const currentUserEmail = get().userEmail;
      set({
        workspaceId: id,
        hasWorkspaceContext: Boolean(id && currentUserEmail),
      });
    },
    clearWorkspaceId: () => {
      clearStoredWorkspaceId();
      set({
        workspaceId: null,
        hasWorkspaceContext: false,
      });
    },
    syncUserEmail: () => {
      const authData = getStoredExpertAuthData();
      const workspaceId = get().workspaceId;
      const userEmail = getDefaultEmail(authData?.accountant?.emails) || null;
      set({
        userEmail,
        hasWorkspaceContext: Boolean(workspaceId && userEmail),
      });
    },
  };
});

export default useWorkspaceDetails;
